import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Spin, Alert, Button, Typography } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import EditLeadForm from "../components/EditLeadForm";
import api from "../shared/api.jsx";
import { logInfo, logSuccess, logError } from "../shared/utils/logger.js";

const editLeadPath = (token) => `/leads/edit/edit-lead/${token}`;
const publicEditOptions = { skipAuth: true, skipAuthRedirect: true };

const { Title, Text } = Typography;

/**
 * EditLead Page - Token-based lead editing
 * Public route that doesn't require authentication
 */
const EditLead = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [changes, setChanges] = useState({});

  // Load lead data on mount
  useEffect(() => {
    if (token) {
      loadLeadData();
    } else {
      setError("Invalid edit link - no token provided");
      setLoading(false);
    }
  }, [token]);

  /**
   * Load lead data using the token
   */
  const loadLeadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await api.get(editLeadPath(token), publicEditOptions);

      if (!data?.success) {
        throw new Error(data?.error || "Failed to load lead data");
      }

      setLeadData(data.lead);
      setTokenData(data.token);

      logInfo("EditLead: data loaded for editing", {
        leadId: data.lead?._id,
      });
    } catch (err) {
      logError("EditLead: error loading lead data", err);
      setError(getErrorMessage(err.message));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setError(null);

      const data = await api.put(
        editLeadPath(token),
        { leadData: formData },
        publicEditOptions
      );

      if (!data?.success) {
        throw new Error(data?.error || "Failed to update lead");
      }

      // Success!
      setChanges(data.changes || {});
      setSuccess(true);

      logSuccess("EditLead: lead updated successfully", {
        leadId: data.leadId,
        changes: data.changes,
      });

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      logError("EditLead: error updating lead", err);
      setError(getErrorMessage(err.message));
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Get user-friendly error message
   */
  const getErrorMessage = (message) => {
    if (message.includes("expired")) {
      return "Your edit link has expired. Please request a new one by messaging us on WhatsApp.";
    }
    if (message.includes("already used")) {
      return "This edit link has already been used. Please request a new one if you need to make more changes.";
    }
    if (message.includes("not editable")) {
      return "This request can no longer be edited as it has been processed.";
    }
    if (message.includes("Rate limit")) {
      return "Too many edit requests. Please try again tomorrow.";
    }
    return message || "An error occurred. Please try again or contact support.";
  };

  /**
   * Handle going back to WhatsApp
   */
  const handleBackToWhatsApp = () => {
    // Try to open WhatsApp, fallback to home page
    const callNumber = import.meta.env.VITE_CALL_NUMBER || "0676744632";
    const digits = (callNumber || "").replace(/\D/g, "");
    const normalized = digits.startsWith("0") ? `27${digits.slice(1)}` : digits;
    const whatsappUrl = `https://wa.me/${normalized}`;
    window.open(whatsappUrl, "_blank");
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spin size="large" />
          <Text style={{ marginTop: 16, fontSize: "16px" }}>
            Loading your request...
          </Text>
        </LoadingContainer>
      </Container>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <Alert
            message="Unable to Load Request"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToWhatsApp}
            size="large"
          >
            Back to WhatsApp
          </Button>
        </ErrorContainer>
      </Container>
    );
  }

  /**
   * Render success state
   */
  if (success) {
    return (
      <Container>
        <SuccessContainer>
          <CheckCircleOutlined
            style={{ fontSize: "48px", color: "#52c41a", marginBottom: 16 }}
          />
          <Title level={2} style={{ color: "#52c41a", marginBottom: 8 }}>
            Changes Saved Successfully!
          </Title>
          <Text
            style={{ fontSize: "16px", marginBottom: 24, textAlign: "center" }}
          >
            Your moving request has been updated. You&apos;ll receive a new
            quote within 2 hours.
          </Text>

          {Object.keys(changes).length > 0 && (
            <ChangesContainer>
              <Text
                strong
                style={{ fontSize: "16px", marginBottom: 12, display: "block" }}
              >
                Changes Made:
              </Text>
              {Object.entries(changes).map(([field, change]) => (
                <ChangeItem key={field}>
                  <Text strong>{formatFieldName(field)}:</Text>
                  <br />
                  <Text delete style={{ color: "#ff4d4f" }}>
                    {formatFieldValue(field, change.from)}
                  </Text>
                  <br />
                  <Text style={{ color: "#52c41a" }}>
                    {formatFieldValue(field, change.to)}
                  </Text>
                </ChangeItem>
              ))}
            </ChangesContainer>
          )}

          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToWhatsApp}
            size="large"
            style={{ marginTop: 16 }}
          >
            Back to WhatsApp
          </Button>
        </SuccessContainer>
      </Container>
    );
  }

  /**
   * Render edit form
   */
  return (
    <Container>
      <Header>
        <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
          <EditOutlined style={{ marginRight: 8 }} />
          Edit Your Moving Request
        </Title>
        <RequestInfo>
          <Text strong>Reference: #{leadData.referenceNumber}</Text>
          <Text type="secondary">
            Expires: {new Date(tokenData.expiresAt).toLocaleString()}
          </Text>
        </RequestInfo>
      </Header>

      <FormContainer>
        <EditLeadForm
          initialData={leadData}
          onSubmit={handleSubmit}
          loading={submitting}
          error={error}
        />
      </FormContainer>
    </Container>
  );
};

// Helper functions
const formatFieldName = (field) => {
  const fieldNames = {
    from: "From Location",
    to: "To Location",
    date: "Moving Date",
    items: "Items",
    notes: "Notes",
  };
  return fieldNames[field] || field;
};

const formatFieldValue = (field, value) => {
  if (field === "date") {
    // Handle string-based dates (like "Not sure", "Urgently")
    if (typeof value === "string") {
      return value || "Not set";
    }
    // Handle actual Date objects
    return value ? new Date(value).toLocaleDateString() : "Not set";
  }
  if (field === "items") {
    return Array.isArray(value) ? value.join(", ") : value || "None";
  }
  return value || "Not set";
};

// Styled components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 8px;

  @media (min-width: 480px) {
    padding: 16px;
  }

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const ErrorContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SuccessContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const Header = styled.div`
  background: white;
  border-radius: 12px 12px 0 0;
  padding: 16px;

  @media (min-width: 480px) {
    padding: 20px;
  }

  @media (min-width: 768px) {
    padding: 24px;
  }

  border-bottom: 1px solid #f0f0f0;
`;

const RequestInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 0 0 12px 12px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const ChangesContainer = styled.div`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  text-align: left;
`;

const ChangeItem = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export default EditLead;
